import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import axios from 'axios';
import {
  ConfirmDialog,
  EmptyState,
  LoadingState,
  PageHeader,
  ResponsiveTable,
  StatusChip,
  useFeedback,
} from '../components/ui';
import { canSeeManagerNavigation, readSessionUser } from '../utils/sessionUser';

import { API_BASE_URL } from '../config/apiBase';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

export default function PointApproval() {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const { notify } = useFeedback();
  const session = readSessionUser();
  const canValidate = canSeeManagerNavigation(session) && session?.tipo !== 'funcionario';
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pontos, setPontos] = useState([]);
  const [decision, setDecision] = useState(null);
  const [observacoes, setObservacoes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const carregar = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/ponto/pendentes`, { headers: authHeaders() });
      setPontos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setPontos([]);
      setLoadError(error?.response?.status === 401
        ? 'Sessão expirada. Entre novamente.'
        : 'Não foi possível carregar a fila.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const validar = async () => {
    if (!decision || submitting) return;
    setSubmitting(true);
    try {
      await axios.put(`${API_BASE_URL}/ponto/validar/${decision.ponto.id}`, {
        status: decision.status,
        observacoes_validacao: observacoes,
      }, { headers: authHeaders() });
      notify(decision.status === 'aprovado' ? 'Ponto aprovado.' : 'Ponto rejeitado.', 'success');
      setDecision(null);
      setObservacoes('');
      await carregar();
    } catch (error) {
      notify(error?.response?.status === 401
        ? 'Sessão expirada. Entre novamente.'
        : (error?.response?.data?.message || 'Não foi possível validar o ponto.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        title="Aprovação de pontos"
        subtitle="Fila pendente atual. O histórico de pontos já processados não faz parte desta tela."
        actions={<Button variant="outlined" onClick={carregar} disabled={loading}>Atualizar</Button>}
      />

      {loading && pontos.length === 0 ? <LoadingState label="Carregando pendências" /> : null}
      {!loading && loadError ? (
        <EmptyState title={loadError} action={<Button onClick={carregar}>Tentar novamente</Button>} />
      ) : null}
      {!loadError && !loading && pontos.length === 0 ? (
        <EmptyState title="Nenhum ponto pendente no momento." />
      ) : null}
      {pontos.length > 0 ? (
        compact ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {pontos.map((ponto) => (
              <Box key={ponto.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{ponto.nome_usuario || 'Sua marcação'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {ponto.tipo === 'saida' ? 'Saída' : 'Entrada'} · {formatDateTime(ponto.data_hora)}
                </Typography>
                <Typography variant="body2" color="text.secondary">{ponto.observacoes || 'Sem observação'}</Typography>
                {canValidate ? <DecisionButtons ponto={ponto} onDecide={setDecision} disabled={submitting} /> : null}
              </Box>
            ))}
          </Box>
        ) : (
          <ResponsiveTable minWidth={760} label="Pontos pendentes">
            <TableHead>
              <TableRow>
                <TableCell>Usuário</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Data/hora</TableCell>
                <TableCell>Localização</TableCell>
                <TableCell>Observações</TableCell>
                {canValidate ? <TableCell>Ações</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {pontos.map((ponto) => (
                <TableRow key={ponto.id} hover>
                  <TableCell>{ponto.nome_usuario || 'Sua marcação'}</TableCell>
                  <TableCell><StatusChip status={ponto.tipo} label={ponto.tipo === 'saida' ? 'Saída' : 'Entrada'} /></TableCell>
                  <TableCell>{formatDateTime(ponto.data_hora)}</TableCell>
                  <TableCell>
                    {ponto.latitude != null && ponto.longitude != null
                      ? `${Number(ponto.latitude).toFixed(4)}, ${Number(ponto.longitude).toFixed(4)}`
                      : '—'}
                  </TableCell>
                  <TableCell>{ponto.observacoes || '—'}</TableCell>
                  {canValidate ? (
                    <TableCell>
                      <DecisionButtons ponto={ponto} onDecide={setDecision} disabled={submitting} />
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </ResponsiveTable>
        )
      ) : null}

      <ConfirmDialog
        open={Boolean(decision)}
        title={decision?.status === 'rejeitado' ? 'Rejeitar ponto' : 'Aprovar ponto'}
        description={decision ? `${decision.ponto.nome_usuario || 'Marcação'} · ${decision.ponto.tipo === 'saida' ? 'Saída' : 'Entrada'} · ${formatDateTime(decision.ponto.data_hora)}` : ''}
        confirmLabel={decision?.status === 'rejeitado' ? 'Rejeitar' : 'Aprovar'}
        destructive={decision?.status === 'rejeitado'}
        confirming={submitting}
        onConfirm={validar}
        onClose={() => { if (!submitting) setDecision(null); }}
      >
        <TextField
          label="Observação da validação"
          value={observacoes}
          onChange={(event) => setObservacoes(event.target.value)}
          fullWidth
          size="small"
          sx={{ mt: 2 }}
        />
      </ConfirmDialog>
    </Box>
  );
}

function DecisionButtons({ ponto, onDecide, disabled }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <Button size="small" variant="contained" disabled={disabled} onClick={() => onDecide({ ponto, status: 'aprovado' })} aria-label={`Aprovar ponto de ${ponto.nome_usuario || 'marcação'}`}>
        Aprovar
      </Button>
      <Button size="small" variant="outlined" color="error" disabled={disabled} onClick={() => onDecide({ ponto, status: 'rejeitado' })} aria-label={`Rejeitar ponto de ${ponto.nome_usuario || 'marcação'}`}>
        Rejeitar
      </Button>
    </Box>
  );
}
